import { useEffect, useMemo, useState } from 'react';

import Character from './Character';
import { buildConfetti, Confetti, getTeamColor, getContrastText, getDanceForIndex } from './helpers';

// Phased Winners & Losers celebration.
//   0 — Tough loss:       losers sad, holding their old flags; winners wait.
//   1 — Invitation:       winners open arms + speech bubble.
//   2 — Letting go:       losers' flags drop off their poles.
//   3 — On the way:       losers run over toward the winning team.
//   4 — New colors:       losers receive the winning team's flag.
//   5 — Stronger together: everyone celebrates with matching flags.
const PHASE_KICKER = [
  'Results are in',
  'A new chapter',
  'Letting go',
  'On the way',
  'New colors',
  'Stronger together'
];
const PHASE_TITLE = [
  'Tough loss.',
  "But you're invited.",
  'Leaving it behind.',
  'Here we come!',
  'Welcome aboard.',
  'One team now.'
];

const PHASE_TIMINGS = [
  { phase: 1, at: 2500 },
  { phase: 2, at: 4500 },
  { phase: 3, at: 5600 },
  { phase: 4, at: 7600 },
  { phase: 5, at: 9000 }
];

// DroppedFlag sits on the ground once its owner has let it go.
function DroppedFlag({ color, name }) {
  return (
    <svg width="90" height="34" viewBox="0 0 90 30" xmlns="http://www.w3.org/2000/svg">
      <line x1="2" y1="22" x2="82" y2="22" stroke="#1a1a1a" strokeWidth="2" strokeLinecap="round" />
      <circle cx="82" cy="22" r="2.5" fill="#1a1a1a" />
      <path
        d="M 6 22 L 54 22 Q 57 24 54 26 L 52 28 Q 57 29 52 30 L 6 30 Z"
        fill={color}
        opacity="0.65"
        stroke="#1a1a1a"
        strokeWidth="1"
      />
      <text
        x="30"
        y="28"
        textAnchor="middle"
        fontFamily="Inter, sans-serif"
        fontSize="10"
        fontWeight="800"
        fill={getContrastText(color)}
        letterSpacing="0.4"
        opacity="0.9"
      >
        {name.toUpperCase()}
      </text>
    </svg>
  );
}

export default function WinnersLosers({ winners = [], losers = [], roundName = '' }) {
  const [phase, setPhase] = useState(0);
  const [phaseConfetti, setPhaseConfetti] = useState([]);

  // Stable loser→winner assignment — every loser follows one winner round-robin.
  const assignments = useMemo(() => {
    if (!winners.length) return [];
    return losers.map((loser, i) => ({
      loser,
      winnerIndex: i % winners.length,
      winner: winners[i % winners.length]
    }));
  }, [losers, winners]);

  useEffect(() => {
    const timers = PHASE_TIMINGS.map(({ phase: p, at }) =>
      setTimeout(() => setPhase(p), at)
    );
    const confettiTimer = setTimeout(() => setPhaseConfetti(buildConfetti(50)), 9200);
    return () => {
      timers.forEach(clearTimeout);
      clearTimeout(confettiTimer);
    };
  }, []);

  // Horizontal stage layout (percent-of-stage). Losers start on the left,
  // winners on the right. On phase 3 each loser slides to a position next
  // to the winner they're joining.
  const loserStartX = (i) => 16 + i * Math.min(16, 60 / Math.max(losers.length, 1));
  const winnerX = (i) => 62 + i * Math.min(16, 32 / Math.max(winners.length, 1));

  return (
    <>
      <Confetti pieces={phaseConfetti} />

      <div className="celebration-header">
        <div className="celebration-kicker">
          {roundName ? `${roundName} — ${PHASE_KICKER[phase]}` : PHASE_KICKER[phase]}
        </div>
        <h1 className="celebration-title celebration-fade-in-up" key={phase}>
          {PHASE_TITLE[phase]}
        </h1>
      </div>

      <div className="celebration-stage">
        <div className="celebration-stage-ground" />
        <div
          className="celebration-stage-divider"
          style={{ opacity: phase === 0 ? 1 : 0 }}
        />

        {/* Dropped flags litter the starting positions once phase 2 hits */}
        {phase >= 2 && losers.map((loser, i) => (
          <div
            key={`dropped-${loser.id}`}
            className="celebration-dropped-flag"
            style={{ left: `${loserStartX(i) - 3}%` }}
          >
            <DroppedFlag color={getTeamColor(loser.name)} name={loser.name} />
          </div>
        ))}

        {/* Winners — stand, bounce once invitation starts, flag waves throughout */}
        {winners.map((winner, i) => {
          const winnerTeamColor = getTeamColor(winner.name);
          const winnerDance = phase >= 1 ? getDanceForIndex(i) : 'idle';
          const winnerPose = phase === 0
            ? 'neutral'
            : (phase === 1 || phase === 3 ? 'armsOut' : 'armsUp');
          return (
            <div
              key={winner.id}
              className="celebration-stage-actor"
              style={{ left: `${winnerX(i)}%` }}
            >
              <Character
                color={winnerTeamColor}
                teamName={winner.name}
                showFlag
                flagWaving
                dance={winnerDance}
                pose={winnerPose}
                size="medium"
              />
              <div className="celebration-winners-tag">Winners</div>
            </div>
          );
        })}

        {/* Losers — sad at first, then transition through neutral → running
            → receiving → celebrating. They physically move to their winner
            target in phase 3 and pick up the winning team's flag in phase 4.
            On phase 5 their body color shifts to the winning team's color —
            they've fully joined the new squad. */}
        {assignments.map(({ loser, winner, winnerIndex }, i) => {
          const isBeforeMove = phase < 3;
          const isMoving = phase === 3;
          const left = isBeforeMove
            ? loserStartX(i)
            : winnerX(winnerIndex) - 9;

          const hasOriginalFlag = phase < 2;
          const hasNewFlag = phase >= 4;
          const currentFlagName = hasOriginalFlag ? loser.name : winner.name;

          const pose =
            phase === 0 ? 'sad'
            : phase === 4 ? 'armsOut'
            : phase === 5 ? 'armsUp'
            : 'neutral';

          const loserColor = phase === 5 ? getTeamColor(winner.name) : getTeamColor(loser.name);
          const loserDance = phase === 5 ? getDanceForIndex(i) : 'idle';

          return (
            <div
              key={loser.id}
              className="celebration-stage-actor"
              style={{ left: `${left}%` }}
            >
              <Character
                color={loserColor}
                teamName={hasOriginalFlag || hasNewFlag ? currentFlagName : ''}
                showFlag={hasOriginalFlag || hasNewFlag}
                flagWaving
                droppingFlag={phase === 2}
                running={isMoving}
                dance={loserDance}
                pose={pose}
                size="medium"
              />

              {phase === 5 && (
                <div
                  className="celebration-heart"
                  style={{ animationDelay: `${i * 0.2}s` }}
                  aria-hidden="true"
                >
                  ♥
                </div>
              )}
            </div>
          );
        })}

        {/* Invitation speech bubble — winners calling losers over */}
        {phase === 1 && (
          <div className="celebration-speech-bubble">Come join our team!</div>
        )}
      </div>

      {/* Progress dots so the viewer knows the sequence is advancing */}
      <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '24px' }}>
        {[0, 1, 2, 3, 4, 5].map(p => (
          <div
            key={p}
            style={{
              width: phase === p ? '28px' : '8px',
              height: '8px',
              borderRadius: '4px',
              background: phase >= p ? '#2F4166' : '#DAE5E8',
              transition: 'all 0.4s ease'
            }}
          />
        ))}
      </div>
    </>
  );
}
